import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Table, Badge, Dropdown } from "react-bootstrap";
import { useLocation, useParams } from "react-router-dom";
import { Search, MoreVertical } from "lucide-react";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import { getEventTasks, updateUserTasks } from "../../services/UserServices/UserService";
import PageTitle from "../layouts/PageTitle";
import Pagination from "../components/Pagination";
import { TableEmptyRow, TableLoaderRow } from "../components/TableLoader";

export default function Event_tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const location = useLocation();
  const historyData = location.state;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getTasks = async () => {
    try {
      setLoading(true);
      const res = await getEventTasks(currentPage, 10, id, debouncedSearch);
      setTasks(res?.data?.data?.event || []);
      setTotalItems(res?.data?.data?.count || 0);
    } catch (error) {
      console.error(error?.response?.data?.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getTasks();
  }, [debouncedSearch, currentPage, id]);

  const debouncedHandler = useMemo(
    () =>
      debounce((value) => {
        if (value.length >= 1 || value.length === 0) {
          setDebouncedSearch(value);
          setCurrentPage(0);
        }
      }, 800),
    []
  );

  useEffect(() => {
    return () => {
      debouncedHandler.cancel();
    };
  }, [debouncedHandler]);

  const handleStatus = async (taskId) => {
    try {
      await updateUserTasks(taskId);
      getTasks();
      toast.success("Status updated successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message);
    }
  };

  const priorityBadge = (priority = "") => {
    const value = priority.toLowerCase();
    const label = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
    if (value === "high") return <Badge bg="danger">{label}</Badge>;
    if (value === "medium") return <Badge bg="warning" text="dark">{label}</Badge>;
    if (value === "low") return <Badge bg="info">{label}</Badge>;
    return <Badge bg="secondary">{label || "—"}</Badge>;
  };

  return (
    <div className="qx-page">
      <PageTitle
        breadcrumbs={[
          { label: "Event List", path: "/events" },
          {
            label: historyData?.name ? historyData?.name : "Event",
            path: `/event_details/${id}`,
            state: { tension: historyData },
          },
          { label: "Tasks" },
        ]}
      />

      <div className="qx-toolbar mb-3">
        <div className="qx-search-wrap qx-search-wrap--icon">
          <Search size={16} className="qx-search-icon" />
          <input
            type="text"
            className="form-control"
            placeholder="Search tasks by name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              debouncedHandler(e.target.value);
            }}
          />
          {search ? (
            <button
              type="button"
              className="qx-search-clear"
              aria-label="Clear search"
              onClick={() => {
                setSearch("");
                debouncedHandler("");
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      <Row>
        <Col>
          <Card className="qx-table-card">
            <Card.Body>
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th>
                      <strong>S.NO</strong>
                    </th>
                    <th>
                      <strong>Task Name</strong>
                    </th>
                    <th>
                      <strong>Event Name</strong>
                    </th>
                    <th>
                      <strong>Assigned To</strong>
                    </th>
                    <th>
                      <strong>Priority</strong>
                    </th>
                    <th>
                      <strong>Due Date</strong>
                    </th>
                    <th>
                      <strong>Status</strong>
                    </th>
                    <th>
                      <strong>Action</strong>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TableLoaderRow colSpan={8} message="Loading tasks..." />
                  ) : !tasks?.length ? (
                    <TableEmptyRow colSpan={8} message="No tasks found" />
                  ) : (
                    tasks.map((task, index) => (
                      <tr key={task?._id || index}>
                        <td>{index + 1 + currentPage * itemsPerPage}</td>
                        <td>{task.name}</td>
                        <td>{task?.event?.name?.name || "—"}</td>
                        <td>
                          {task.assigningToGuest?.name
                            ? task.assigningToGuest?.name
                            : "Not Assigned"}
                        </td>
                        <td>{priorityBadge(task.priority)}</td>
                        <td>
                          {task?.dueDate
                            ? new Date(task.dueDate).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td>
                          {task?.status?.toLowerCase() === "upcoming" ? (
                            <Badge bg="secondary">
                              {task.status.charAt(0).toUpperCase() +
                                task.status.slice(1).toLowerCase()}
                            </Badge>
                          ) : (
                            <Badge bg="success">{task?.status}</Badge>
                          )}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="info light"
                              className="qx-action-toggle"
                              aria-label="Task actions"
                            >
                              <MoreVertical size={16} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleStatus(task?._id)}>
                                {task?.status === "upcoming"
                                  ? "Mark as Completed"
                                  : "Mark as Upcoming"}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {!loading && tasks?.length > 0 && (
                <Pagination
                  pageCount={totalPages}
                  pageValue={currentPage}
                  setPage={setCurrentPage}
                  total={totalItems}
                  pageSize={itemsPerPage}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
